import { useEffect, useState } from 'react';
import { Alert, Button, Group, Stack, Switch, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';
import { FormModal } from '../../components/ui/FormModal';
import { companiesApi } from '../../services/companiesApi';
import { getApiErrorMessage, getApiViolations } from '../../utils/errorHandling';
import { mapViolations } from '../../utils/mapViolations';

interface CreateCompanyModalProps {
  opened: boolean;
  onClose: () => void;
  /** Laeuft nach dem POST und vor dem Schliessen -- der Aufrufer laedt hier neu und waehlt das
   * neue Unternehmen aus. */
  onCreated: (companyId: number) => Promise<void>;
}

/**
 * Anlegen-Dialog fuer ein Unternehmen. Ersetzt das frueher sofortige POST mit dem Platzhalternamen
 * "Neues Unternehmen N" (Gegenstueck zu `CreatePlantModal`).
 */
export function CreateCompanyModal({ opened, onClose, onCreated }: CreateCompanyModalProps) {
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const form = useForm({
    mode: 'controlled',
    initialValues: { name: '', csiMember: false },
  });

  useEffect(() => {
    if (opened) {
      form.reset();
      setError(null);
    }
    // form ist ueber die Lebenszeit der Komponente stabil; nur das Oeffnen soll zuruecksetzen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  async function handleSubmit(values: { name: string; csiMember: boolean }) {
    setError(null);
    setSaving(true);
    try {
      const created = await companiesApi.create({
        name: values.name.trim(),
        csiMember: values.csiMember,
      });
      await onCreated(created.id);
      onClose();
    } catch (err) {
      form.setErrors(mapViolations(getApiViolations(err)));
      setError(getApiErrorMessage(err, 'Fehler beim Anlegen des Unternehmens'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <FormModal opened={opened} onClose={onClose} title="Unternehmen anlegen">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red">
              {error}
            </Alert>
          )}
          <TextInput
            label="Name"
            placeholder="Unternehmensname"
            data-autofocus
            required
            {...form.getInputProps('name')}
          />
          <Switch label="CSI-Mitglied" {...form.getInputProps('csiMember', { type: 'checkbox' })} />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={!form.getValues().name.trim()} loading={saving}>
              Unternehmen anlegen
            </Button>
          </Group>
        </Stack>
      </form>
    </FormModal>
  );
}
